import { configureQuery, EditCommandsEnum, useNotifications } from '@diana/core';
import { FLOW_BUILDER2_PAGE_NS } from '../i18n';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useFlowBuilder2PageStore } from '../Store/useFlowBuilder2PageStore';
import { useReloadBuilder } from './useReloadBuilder';

export interface IUseAiAcceptRejectProps {
  onSuccess?: () => void;
  onError?: () => void;
  onFinally?: () => void;
}

export const useAiAcceptRejectCommands = (props: IUseAiAcceptRejectProps) => {
  const { onSuccess, onError, onFinally } = props;
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const notification = useNotifications();
  const reloadBuilder = useReloadBuilder();

  const aiPendingStatuses = useFlowBuilder2PageStore((s) => s.aiPendingStatuses);
  const resetEditPending = useFlowBuilder2PageStore((s) => s.resetEditPending);

  const getErrorMessage = (command: EditCommandsEnum) => {
    return command === EditCommandsEnum.Accept
      ? t('errors.acceptFailed')
      : t('errors.discardFailed');
  };

  const handleSuccess = ({ updateCache }: { updateCache: boolean }) => {
    resetEditPending();
    if (updateCache) reloadBuilder();
  };

  const editFlowQuery = configureQuery.editFlow();
  const editLayoutQuery = configureQuery.editLayout();

  const { mutateAsync: updateFlowPendingStatus } = useMutation({
    mutationKey: editFlowQuery.key,
    mutationFn: editFlowQuery.fn,
  });

  const { mutateAsync: updateLayoutPendingStatus } = useMutation({
    mutationKey: editLayoutQuery.key,
    mutationFn: editLayoutQuery.fn,
  });

  const updatePendingStatus = ({
    command,
    updateCache = false,
  }: {
    command: EditCommandsEnum;
    updateCache?: boolean;
  }) => {
    const { flows, layouts } = aiPendingStatuses;

    const flowPromises = flows.map((flowId) => updateFlowPendingStatus({ command, flowId }));
    const layoutPromises = layouts.map((layoutId) =>
      updateLayoutPendingStatus({ command, layoutId }),
    );

    const promises = [...flowPromises, ...layoutPromises];

    Promise.all(promises)
      .then(() => {
        handleSuccess({ updateCache });
        onSuccess?.();
      })
      .catch(() => {
        notification.error({ message: getErrorMessage(command) });
        onError?.();
      })
      .finally(() => {
        onFinally?.();
      });
  };

  return { updatePendingStatus };
};
